import { msg } from "@lingui/core/macro";
import {
  type DialogCloseButtonProps,
  type DialogRootProps,
  Dialog as KobalteDialog,
} from "@kobalte/core/dialog";
import { createSignal, type JSX, onCleanup, onMount, splitProps } from "solid-js";
import { ArrowDown, X } from "@fleet/icons";
import { cn } from "@agape/shared";
import { useI18n } from "@/providers/i18n-provider";
import { useInteractionFlag } from "@fleet/ui";
import { useBackDismiss } from "@/lib/use-back-dismiss";
import { isEditingActive } from "@/lib/dom/editable";

const PULL_DOWN_RESISTANCE_EXPONENT = 0.96;
const PULL_UP_RESISTANCE_MULTIPLIER = 0.15;
const DISMISS_DRAG_THRESHOLD_PX = 100;
const INDICATOR_SHOW_THRESHOLD_PX = 15;

interface DialogProps extends DialogRootProps {
  children?: JSX.Element;
  contentClass?: string;
  overlayClass?: string;
  dragToDismissDisabled?: boolean;
  /**
   * ARIA role of the dialog surface. Defaults to `"dialog"` — the correct role
   * for content/form dialogs and what `getByRole("dialog")` matches. Genuine
   * confirmations (see {@link ConfirmDialog}) pass `"alertdialog"`.
   *
   * NOTE: this must be set explicitly. With no `role`, Kobalte's `Dialog.Content`
   * ends up rendering `role="alertdialog"` in this setup, which made every
   * creation/edit form dialog invisible to `getByRole("dialog")` (the `?new=`
   * deep-link e2e were red for exactly this reason — the dialog DID open). CC-17.
   */
  role?: "dialog" | "alertdialog";
}

function extractPaddingClasses(classStr?: string) {
  if (!classStr) {
    return { paddingClasses: "", remainingClasses: "" };
  }

  const words = classStr.split(/\s+/);
  const paddingPatterns = /^!?p[tblrxy]?-(?:\d+(?:\.\d+)?|\[.+\])$/;

  const padding: string[] = [];
  const remaining: string[] = [];

  for (const word of words) {
    if (paddingPatterns.test(word)) {
      padding.push(word);
    } else {
      remaining.push(word);
    }
  }

  return {
    paddingClasses: padding.join(" "),
    remainingClasses: remaining.join(" "),
  };
}

export function Dialog(props: DialogProps) {
  const [local, others] = splitProps(props, [
    "children",
    "contentClass",
    "overlayClass",
    "dragToDismissDisabled",
    "role",
  ]);
  const { t } = useI18n();

  // Every standard dialog raises the shared `dialog.open` flag while open, so
  // shortcuts, gestures and navigation can suppress themselves from one source.
  useInteractionFlag({ "dialog.open": () => others.open === true });

  // Browser back closes the dialog instead of navigating away (PWA pattern).
  useBackDismiss(
    () => others.open === true,
    () => others.onOpenChange?.(false),
  );

  const [translateY, setTranslateY] = createSignal(0);
  const [isDragging, setIsDragging] = createSignal(false);
  const [shouldAnimate, setShouldAnimate] = createSignal(false);

  // Track the VISUAL viewport so the dialog centers within the area that stays
  // visible above the on-screen keyboard on mobile. Without this the content is
  // centered in the layout viewport (full height) and ends up hidden behind the
  // keyboard once an input is focused. No-op on desktop (visual ≈ layout).
  const [viewport, setViewport] = createSignal<{ top: number; height: number } | null>(null);
  onMount(() => {
    const vv = window.visualViewport;
    if (!vv) {
      return;
    }
    const update = () => setViewport({ top: vv.offsetTop, height: vv.height });
    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    onCleanup(() => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    });
  });

  let startY = 0;

  const handleStart = (clientY: number, target: EventTarget | null) => {
    if (local.dragToDismissDisabled || isEditingActive()) {
      return;
    }
    if (!(target instanceof HTMLElement)) {
      return;
    }

    const isInteractive = target.closest("input, select, textarea, button, [role='button'], a");
    if (isInteractive && !target.closest(".drag-handle-bar")) {
      return;
    }

    const scrollable = target.closest(".overflow-y-auto, .overflow-auto");
    if (scrollable && scrollable.scrollTop > 0) {
      return;
    }

    startY = clientY;
    setIsDragging(true);
    setShouldAnimate(false);
  };

  const handleMove = (clientY: number, preventDefault: () => void) => {
    if (!isDragging()) {
      return;
    }
    const deltaY = clientY - startY;

    if (deltaY > 0) {
      setTranslateY(deltaY ** PULL_DOWN_RESISTANCE_EXPONENT);
    } else {
      setTranslateY(deltaY * PULL_UP_RESISTANCE_MULTIPLIER);
    }
    preventDefault();
  };

  const handleEnd = () => {
    if (!isDragging()) {
      return;
    }
    setIsDragging(false);
    setShouldAnimate(true);

    if (translateY() > DISMISS_DRAG_THRESHOLD_PX) {
      others.onOpenChange?.(false);
    }
    setTranslateY(0);
  };

  const paddingData = () => extractPaddingClasses(local.contentClass);

  return (
    <KobalteDialog {...others}>
      <KobalteDialog.Portal>
        <KobalteDialog.Overlay
          class={cn(
            "dialog-overlay fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm",
            local.overlayClass,
          )}
          onMouseDown={(e) => {
            handleStart(e.clientY, e.target);
          }}
          onMouseMove={(e) => {
            handleMove(e.clientY, () => {
              e.preventDefault();
            });
          }}
          onMouseUp={handleEnd}
          onMouseLeave={handleEnd}
          onTouchStart={(e) => {
            const [touch] = e.touches;
            if (touch) {
              handleStart(touch.clientY, e.target);
            }
          }}
          onTouchMove={(e) => {
            const [touch] = e.touches;
            if (touch) {
              handleMove(touch.clientY, () => {
                if (e.cancelable) {
                  e.preventDefault();
                }
              });
            }
          }}
          onTouchEnd={handleEnd}
        />
        <div
          role="presentation"
          class={cn(
            "fixed inset-x-0 z-[210] flex items-center justify-center p-4",
            !viewport() && "inset-y-0",
          )}
          style={
            viewport()
              ? { top: `${viewport()?.top ?? 0}px`, height: `${viewport()?.height ?? 0}px` }
              : undefined
          }
          onMouseDown={(e) => {
            handleStart(e.clientY, e.target);
          }}
          onMouseMove={(e) => {
            handleMove(e.clientY, () => {
              e.preventDefault();
            });
          }}
          onMouseUp={handleEnd}
          onMouseLeave={handleEnd}
          onTouchStart={(e) => {
            const [touch] = e.touches;
            if (touch) {
              handleStart(touch.clientY, e.target);
            }
          }}
          onTouchMove={(e) => {
            const [touch] = e.touches;
            if (touch) {
              handleMove(touch.clientY, () => {
                if (e.cancelable) {
                  e.preventDefault();
                }
              });
            }
          }}
          onTouchEnd={handleEnd}
        >
          <KobalteDialog.Content
            role={local.role ?? "dialog"}
            class={cn(
              "dialog-content w-full rounded-3xl border border-border bg-card shadow-2xl overflow-hidden outline-none select-none relative",
              paddingData().remainingClasses,
            )}
            style={{
              transform: `translateY(${translateY()}px)`,
              transition: shouldAnimate()
                ? "transform 350ms cubic-bezier(0.19, 1, 0.22, 1)"
                : "none",
            }}
          >
            {/* Visual Pull Down Indicator Bar */}
            <div class="drag-handle-bar flex flex-col items-center justify-center pt-3 pb-1 cursor-row-resize shrink-0 select-none">
              <div
                class="flex items-center justify-center gap-1.5 text-[10px] font-bold tracking-wider uppercase opacity-60 mb-2 transition-all duration-300 overflow-hidden text-muted-foreground select-none"
                style={{
                  height: translateY() > INDICATOR_SHOW_THRESHOLD_PX ? "16px" : "0px",
                  opacity: translateY() > INDICATOR_SHOW_THRESHOLD_PX ? 1 : 0,
                  color:
                    translateY() >= DISMISS_DRAG_THRESHOLD_PX
                      ? "hsl(var(--destructive))"
                      : "hsl(var(--muted-foreground))",
                }}
              >
                <ArrowDown
                  class={cn(
                    "h-3 w-3 transition-transform duration-300",
                    translateY() >= DISMISS_DRAG_THRESHOLD_PX
                      ? "rotate-180 text-destructive scale-125"
                      : "",
                  )}
                />
                <span>
                  {translateY() >= DISMISS_DRAG_THRESHOLD_PX
                    ? t(msg`Release to cancel`)
                    : t(msg`Pull down to cancel`)}
                </span>
              </div>
              <div
                class={cn(
                  "w-12 h-1.5 rounded-full bg-muted-foreground/35 transition-all duration-300",
                  translateY() >= DISMISS_DRAG_THRESHOLD_PX
                    ? "bg-destructive w-16"
                    : "group-hover:bg-muted-foreground/60",
                )}
              />
            </div>

            <div class={cn("flex-1 min-h-0", paddingData().paddingClasses)}>{local.children}</div>
          </KobalteDialog.Content>
        </div>
      </KobalteDialog.Portal>
    </KobalteDialog>
  );
}

export const DialogTitle = KobalteDialog.Title;
export const DialogDescription = KobalteDialog.Description;

interface DialogCloseProps extends DialogCloseButtonProps {
  class?: string;
  children?: JSX.Element;
  size?: "sm" | "md";
}

export function DialogClose(props: DialogCloseProps) {
  const [local, others] = splitProps(props, ["class", "children", "size"]);
  const size = () => local.size ?? "sm";
  const { t } = useI18n();

  return (
    <KobalteDialog.CloseButton
      // A11y: Kobalte's built-in fallback name is the untranslated English
      // "Dismiss"; provide a localized label. `{...others}` spreads after, so
      // callers can still override it.
      aria-label={t(msg`Close`)}
      class={cn(
        "rounded-full hover:bg-accent transition-colors cursor-pointer",
        size() === "sm" ? "p-1" : "p-2",
        local.class,
      )}
      {...others}
    >
      {local.children ?? <X class={size() === "sm" ? "h-4 w-4" : "h-5 w-5"} />}
    </KobalteDialog.CloseButton>
  );
}

interface DialogHeaderProps extends JSX.HTMLAttributes<HTMLDivElement> {
  children?: JSX.Element;
  size?: "sm" | "md";
  showClose?: boolean;
  border?: boolean;
}

export function DialogHeader(props: DialogHeaderProps) {
  const [local, others] = splitProps(props, ["class", "children", "size", "showClose", "border"]);
  const size = () => local.size ?? "sm";
  const showClose = () => local.showClose ?? true;
  const border = () => local.border ?? true;

  return (
    <div
      class={cn(
        "flex items-center justify-between shrink-0",
        border() && "border-b border-border/10",
        size() === "sm" ? "p-4" : "p-6",
        local.class,
      )}
      {...others}
    >
      {local.children}
      {showClose() && <DialogClose size={size()} />}
    </div>
  );
}
